import { useMemo, useState } from 'react';
import { Modal, Stack, Group, Button, Select, NumberInput, Alert, Text } from '@mantine/core';
import { api } from '../../api/client';
import type { OrderPart } from './OrderPartModal';
import type { QuotationPart } from './NewQuotationModal';

export default function AddPartToQuotationModal({
  orderId,
  quotationId,
  parts,
  existing,
  onClose,
  onDone,
}: {
  orderId: number;
  quotationId: number;
  parts: OrderPart[];
  existing: QuotationPart[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [partId, setPartId] = useState<string | null>(null);
  const [qty, setQty] = useState<number | string>(1);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const available = useMemo(() => {
    // Compute remaining for each part = part.quantity - sum(existing quotation parts quantity).
    return parts.map((p) => {
      const allocated = existing.filter((qp) => qp.orderPartId === p.id).reduce((s, qp) => s + qp.quantity, 0);
      return { part: p, remaining: Math.max(0, p.quantity - allocated) };
    });
  }, [parts, existing]);

  const selected = available.find((a) => String(a.part.id) === partId);
  const max = selected?.remaining ?? 0;

  async function save() {
    setErr(null);
    if (!partId) return setErr('Pick a part.');
    if (!selected || max <= 0) return setErr('No quantity left for this part.');
    const n = Math.max(1, Math.min(max, Math.round(Number(qty) || 1)));
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/quotations/${quotationId}/parts`, { orderPartId: Number(partId), quantity: n });
      onDone();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal opened onClose={onClose} title={<span className="modal-title-custom">Add Part to Quotation</span>}>
      <Stack>
        <Select
          label="Part"
          placeholder="Select a part from this order"
          data-tour="qp-part"
          data={available.filter((a) => a.remaining > 0).map((a) => ({
            value: String(a.part.id),
            label: `${a.part.partUid} — ${a.part.name} (${a.remaining} left)`,
          }))}
          value={partId}
          onChange={(v) => { setPartId(v); setQty(1); }}
          searchable
        />
        <NumberInput
          label="Quantity"
          data-tour="qp-qty"
          value={qty}
          onChange={setQty}
          min={1}
          max={max || 1}
          step={1}
          disabled={!selected}
        />
        {selected && (
          <Text size="xs" c="dimmed">Remaining after save: {Math.max(0, max - (Math.round(Number(qty) || 1)))} of {selected.part.quantity}.</Text>
        )}
        {err && <Alert color="red">{err}</Alert>}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button data-tour="qp-save" onClick={save} loading={busy} disabled={!selected || max <= 0}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
